import { NgModule, ErrorHandler, ModuleWithProviders, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { AccordionModule } from 'primeng/accordion';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { ChipModule  } from 'primeng/chip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { SplitButtonModule } from 'primeng/splitbutton';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from "primeng/radiobutton";
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';

import { MessageService as PrimeNgMessageService } from 'primeng/api';

import { ConfirmationButtonComponent } from './components/confirmation-button/confirmation-button.component';
import { LocationSearchComponent } from './components/location-search/location-search.component';
import { FileImportComponent } from './components/file-import/file-import.component';
import { ThemeBreadcrumbComponent } from './theme/theme.breadcrumb.component';
import { ThemeComponent } from './theme/theme.component';
import { ThemeFooterComponent } from './theme/theme.footer.component';
import { ThemeMenuComponent, ThemeSubMenuComponent } from './theme/theme.menu.component';
import { ThemeHeaderComponent } from './theme/theme.header.component';
import { ErrorBlockComponent } from './components/error-block/error-block.component';
import { TimeEntryComponent } from './components/time-entry/time-entry.component';
import { ValidationMessageComponent } from './components/validation-message/validation-message.component';
import { PowerBiReportComponent } from './components/power-bi-report/power-bi-report.component';
import { WorkedCalendarComponent } from './components/worked-calendar/worked-calendar.component';
import { HistoryComponent } from './components/history/history.component';
import { AddressNormalizerComponent } from './components/address-normalizer/address-normalizer.component';
import { CustomerSiteDuplicateListComponent } from './components/customer-site-duplicate-list/customer-site-duplicate-list.component';

import { VarDirective } from './directives/var.directive';
import { NumericDirective } from './directives/numeric.directive';
import { RangeDirective } from './directives/range.directive';
import { CamelCasePipe } from './pipes/camel-case.pipe';
import { EnumSelectPipe } from './pipes/enum-select.pipe';
import { EnumLabelPipe } from './pipes/enum-label.pipe';
import { MonthNamePipe } from './pipes/month-name.pipe';
import { MonthNameShortPipe } from './pipes/month-name-short.pipe';
import { TrustedUrlPipe } from './pipes/TrustUrl';
import { GlobalErrorHandler } from './services/global-error-handler';
import { SharedSettings } from './models/shared-settings';
import { BlobStorageComponent } from './components/blob-storage/blob-storage.component';
import { AlphanumericDirective } from './directives/alphanumeric.directive';
import { MessageService } from './services/message-service';
import { DateTimeToLocalTzPipe, DateTimeToTzPipe } from './pipes/datetime-to-local-tz.pipe';
import { ToastComponent } from './components/toast/toast.component';
import { TimeSincePipe } from './pipes/time-since.pipe';
import {TimeBetweenPipe} from "./pipes/time-between.pipe";
import { CdkDragPlaceholder } from "@angular/cdk/drag-drop";
import {
    PalletLoadingIndicatorComponent
} from "./components/pallet-loading-indicator/pallet-loading-indicator.component";
import { UserAvatarComponent } from './components/user-avatar/user-avatar.component';
import { UserAvatarGroupComponent } from './components/user-avatar-group/user-avatar-group.component';

@NgModule({
    declarations: [
        ConfirmationButtonComponent,
        LocationSearchComponent,
        ThemeBreadcrumbComponent,
        ThemeComponent,
        ThemeFooterComponent,
        ThemeMenuComponent,
        ThemeSubMenuComponent,
        ThemeHeaderComponent,
        ErrorBlockComponent,
        TimeEntryComponent,
        ToastComponent,
        ValidationMessageComponent,
        FileImportComponent,
        HistoryComponent,
        PowerBiReportComponent,
        WorkedCalendarComponent,
        AddressNormalizerComponent,
        CustomerSiteDuplicateListComponent,
        PalletLoadingIndicatorComponent,
        UserAvatarComponent,
        UserAvatarGroupComponent,

        CamelCasePipe,
        EnumLabelPipe,
        EnumSelectPipe,
        MonthNamePipe,
        MonthNameShortPipe,
        TrustedUrlPipe,
        TimeSincePipe,
        TimeBetweenPipe,
        VarDirective,
        NumericDirective,
        RangeDirective,
        BlobStorageComponent,
        AlphanumericDirective,
        DateTimeToLocalTzPipe,
        DateTimeToTzPipe,
    ],
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        FormsModule,
        ButtonModule,
        MenuModule,
        BreadcrumbModule,
        AccordionModule,
        PanelModule,
        ToastModule,
        AutoCompleteModule,
        MessageModule,
        ChipModule,
        InputTextModule,
        CardModule,
        SplitButtonModule,
        DialogModule,
        RadioButtonModule,
        AvatarModule,
        TooltipModule,
        SelectModule,
        DatePickerModule
    ],
    providers: [
        MessageService,
        providePrimeNG({
            theme: {
                preset: definePreset(Aura, {
                }),
                options: {
                    cssLayer: {
                        name: 'primeng'
                    }
                }
            }
        }),
        provideHttpClient(withInterceptorsFromDi())
    ],
    exports: [
        ConfirmationButtonComponent,
        LocationSearchComponent,
        ThemeComponent,
        ToastComponent,
        ErrorBlockComponent,
        TimeEntryComponent,
        ValidationMessageComponent,
        FileImportComponent,
        HistoryComponent,
        PowerBiReportComponent,
        WorkedCalendarComponent,
        BlobStorageComponent,
        ToastComponent,

        VarDirective,
        NumericDirective,
        RangeDirective,
        AddressNormalizerComponent,
        PalletLoadingIndicatorComponent,
        UserAvatarComponent,
        UserAvatarGroupComponent,
        CustomerSiteDuplicateListComponent,
        AlphanumericDirective,

        CamelCasePipe,
        MonthNamePipe,
        MonthNameShortPipe,
        EnumSelectPipe,
        EnumLabelPipe,
        TimeSincePipe,
        TrustedUrlPipe,
        DateTimeToLocalTzPipe,
        DateTimeToTzPipe,
        TimeBetweenPipe
    ]
})
export class SharedModule {
    constructor(@Optional() @SkipSelf() parentModule?: SharedModule) {
        if (parentModule) {
            throw new Error('`SharedModule` is already loaded.');
        }
    }

    static forRoot(settings: SharedSettings): ModuleWithProviders<SharedModule> {
        return {
            ngModule: SharedModule,
            providers: [
                { provide: SharedSettings, useValue: settings },
                { provide: ErrorHandler, useClass: GlobalErrorHandler },
                {
                    provide: PrimeNgMessageService,
                    useExisting: MessageService,
                },
            ]
        };
    }
}

